import { Trans } from 'react-i18next';
import { styled } from 'goober';

import { Flexbox } from '@/ui/flexbox/flexbox';
import { Icon } from '@/ui/icons/icon';

export interface SetupStep {
  command?: string;
  hint?: string;
  hintI18nKey?: string;
  text: string;
}

interface McpOAuthSetupStepsProps {
  claudeCodeCommand: string;
  connectorName: string;
  copiedFieldId: string | null;
  copyCommandLabel: string;
  copyCopiedLabel: string;
  copyNameLabel: string;
  mcpUrl: string;
  onCopy: (value: string, fieldId: string) => void;
  steps: SetupStep[];
  stepsKey: 'mcpOAuth.stepsClaudeAiDesktop' | 'mcpOAuth.stepsClaudeCode';
}

const StepsList = styled('ol')`
  color: ${({ theme }) => theme.colors.gray.c12};
  font-family: ${({ theme }) => theme.fonts.neue};
  font-size: 13px;
  line-height: 1.55;
  list-style-position: outside;
  margin: 0 0 4px;
  padding-left: 18px;
`;

const StepItem = styled('li')`
  margin-bottom: 14px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const StepText = styled('span')`
  color: ${({ theme }) => theme.colors.gray.c12};
`;

const StepStrong = styled('strong')`
  color: ${({ theme }) => theme.colors.basics.black};
  font-weight: 600;
`;

const StepArrow = styled('span')`
  color: ${({ theme }) => theme.colors.gray.c8};
  margin: 0 3px;
`;

const CommandField = styled(Flexbox)`
  background-color: ${({ theme }) => theme.colors.spotlight.surface.default};
  border-radius: 8px;
  margin-top: 7px;
  padding: 9px 10px;
`;

const CommandText = styled('code')`
  color: ${({ theme }) => theme.colors.gray.c12};
  flex: 1 1 0;
  font-family: ui-monospace, monospace;
  font-size: 11.5px;
  line-height: 1.45;
  min-width: 0;
  overflow-wrap: anywhere;
`;

const InlineCopyButton = styled('button')`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.spotlight.surface.accent};
  border: none;
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.blue.primaryA};
  cursor: pointer;
  display: inline-flex;
  flex-shrink: 0;
  font-family: ${({ theme }) => theme.fonts.neue};
  font-size: 11.5px;
  font-weight: 500;
  gap: 4px;
  padding: 5px 9px;
  white-space: nowrap;
`;

const StepHint = styled('span')`
  color: ${({ theme }) => theme.colors.gray.c8};
  display: block;
  font-family: ${({ theme }) => theme.fonts.neue};
  font-size: 11.5px;
  line-height: 1.45;
  margin-top: 6px;

  strong {
    color: ${({ theme }) => theme.colors.gray.c10};
    font-weight: 500;
  }
`;

const InlineCode = styled('code')`
  font-family: ui-monospace, monospace;
`;

const HintStrong = styled('strong')`
  color: ${({ theme }) => theme.colors.gray.c10};
  font-weight: 500;
`;

const STEP_TEXT_COMPONENTS = {
  add: <StepStrong />,
  addConnector: <StepStrong />,
  advancedSettings: <HintStrong />,
  arrow: <StepArrow aria-hidden="true" />,
  code: <InlineCode />,
  connectors: <StepStrong />,
  customize: <StepStrong />,
  plus: <StepStrong />,
  savvy: <StepStrong />,
};

const interpolateStepCommand = (
  value: string,
  replacements: {
    claudeCodeCommand: string;
    connectorName: string;
    mcpUrl: string;
  },
): string =>
  value
    .replaceAll('{{claudeCodeCommand}}', replacements.claudeCodeCommand)
    .replaceAll('{{connectorName}}', replacements.connectorName)
    .replaceAll('{{mcpUrl}}', replacements.mcpUrl);

export const McpOAuthSetupSteps = ({
  claudeCodeCommand,
  connectorName,
  copiedFieldId,
  copyCommandLabel,
  copyCopiedLabel,
  copyNameLabel,
  mcpUrl,
  onCopy,
  steps,
  stepsKey,
}: McpOAuthSetupStepsProps) => {
  return (
    <StepsList>
      {steps.map((step, index) => {
        const fieldId = `${stepsKey}-${index}`;
        const commandValue = step.command
          ? interpolateStepCommand(step.command, {
              claudeCodeCommand,
              connectorName,
              mcpUrl,
            })
          : null;
        const copyLabel =
          commandValue === connectorName ? copyNameLabel : copyCommandLabel;

        return (
          <StepItem key={fieldId}>
            <StepText>
              <Trans
                components={STEP_TEXT_COMPONENTS}
                i18nKey={`accessTokensTab.${stepsKey}.${index}.text`}
                ns="settings"
              />
            </StepText>

            {commandValue ? (
              <CommandField alignItems="center" fullWidth gap="8px">
                <CommandText>{commandValue}</CommandText>
                <InlineCopyButton
                  onClick={() => {
                    onCopy(commandValue, fieldId);
                  }}
                  type="button"
                >
                  <Icon
                    color="currentColor"
                    height="13px"
                    icon="Copy"
                    width="13px"
                  />
                  {copiedFieldId === fieldId ? copyCopiedLabel : copyLabel}
                </InlineCopyButton>
              </CommandField>
            ) : null}

            {step.hint || step.hintI18nKey ? (
              <StepHint>
                <Trans
                  components={STEP_TEXT_COMPONENTS}
                  i18nKey={
                    step.hintI18nKey
                      ? `accessTokensTab.${step.hintI18nKey}`
                      : `accessTokensTab.${stepsKey}.${index}.hint`
                  }
                  ns="settings"
                />
              </StepHint>
            ) : null}
          </StepItem>
        );
      })}
    </StepsList>
  );
};
